import type { APIState, Result } from '@api';
import { Failure, Success, useFMSAPI, useErrorMessage } from '@api';
import { projectAtom } from '@data/auth';
import { environmentAtom } from '@data/fms/environment/states';
import type { Vehicle } from '@data/fms/vehicle/types';
import { useNotification } from '@data/notification';
import isNullOrUndefined from '@utils/isNullOrUndefined';
import type { AxiosResponse, AxiosError } from 'axios';
import { useAtomCallback } from 'jotai/utils';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * 地図更新開始リクエストAPI
 */
export const useMapsUpdateAPI = (): {
  state: APIState;
  postMapsUpdate: (vehicleId: string) => Promise<Vehicle | null>;
} => {
  const [state, setState] = useState<APIState>('none');
  const { notifyError, notifySuccess } = useNotification();
  const fmsAPI = useFMSAPI();
  const getErrorMessage = useErrorMessage();
  const { t } = useTranslation();

  const postMapsUpdateAPI = useCallback(
    async (
      projectId: string,
      environmentId: string,
      vehicleId: string,
    ): Promise<Result<Vehicle, AxiosResponse>> => {
      try {
        const res = await fmsAPI.post(
          `/${projectId}/environments/${environmentId}/vehicles/${vehicleId}/maps/update`,
        );
        return new Success(res?.data);
      } catch (error) {
        return new Failure((error as AxiosError).response as AxiosResponse);
      }
    },
    [fmsAPI],
  );

  const postMapsUpdate = useAtomCallback(
    useCallback(
      async (get, _, vehicleId: string) => {
        const project = get(projectAtom);
        const environment = get(environmentAtom);
        if (isNullOrUndefined(project) || isNullOrUndefined(environment)) {
          notifyError({
            message: t('api.fms.post_maps_update', { status: 'failed' }),
          });
          setState('hasError');
          return null;
        }
        setState('loading');
        const res = await postMapsUpdateAPI(
          project!.id,
          environment!.environment_id,
          vehicleId,
        );
        if (isNullOrUndefined(res.value) || res.isFailure()) {
          notifyError({
            message: `${t('api.fms.post_maps_update', {
              status: 'failed',
            })}: ${getErrorMessage(res)}`,
          });
          setState('hasError');
          return null;
        }
        notifySuccess({
          message: t('api.fms.post_maps_update', { status: 'successfully' }),
          testIdAttrs: {
            wrapperTestId: 'vehicle_map_update_snackbar_success-status-div',
            closeButtonTestId:
              'vehicle_map_update_snackbar_success-click-close_button',
          },
        });
        setState('hasValue');
        return res.value;
      },
      [notifyError, notifySuccess, postMapsUpdateAPI, t, getErrorMessage],
    ),
  );

  return {
    state,
    postMapsUpdate,
  };
};
